import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhone, FaMapMarkerAlt } from 'react-icons/fa';
import logo from '../assets/png phoenix dental.png';

const NavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-phoenix-beige/30 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <motion.img
              src={logo}
              alt="Phoenix Dental"
              className="h-14 w-auto"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
            {/* Optional brand text */}
            <span className="hidden sm:block text-lg font-semibold tracking-wide text-phoenix-black">
              Phoenix Dental
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.path;

              return (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.07 }}
                >
                  <Link
                    to={item.path}
                    className={`relative px-4 py-2 text-sm font-medium tracking-wide rounded-md transition-all duration-300
                      ${
                        isActive
                          ? 'text-phoenix-gold'
                          : 'text-phoenix-black hover:text-phoenix-gold'
                      }
                    `}
                  >
                    {item.name}
                    <span
                      className={`absolute left-1/2 -bottom-1 h-0.5 bg-phoenix-gold transition-all duration-300 transform -translate-x-1/2
                        ${isActive ? 'w-3/4' : 'w-0 group-hover:w-3/4'}
                      `}
                    />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {/* Contact Info */}
          <div className="hidden lg:flex items-center gap-6 text-sm">
            <motion.div
              className="flex items-center gap-2 text-phoenix-black"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <FaPhone className="text-phoenix-gold" />
              <a href="tel:+919949042692" className="hover:text-phoenix-gold transition-colors">
                +91 9949042692
              </a>
            </motion.div>
            <motion.div
              className="flex items-center gap-2 text-phoenix-black"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <FaMapMarkerAlt className="text-phoenix-gold" />
              <span>Madhurawada, Visakhapatnam</span>
            </motion.div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsOpen(!isOpen)}
              className="p-3 rounded-lg bg-phoenix-gold text-white shadow-md hover:bg-phoenix-gold/90 transition"
            >
              {isOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-white border-t border-phoenix-beige/30 shadow-lg"
          >
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="block text-base font-medium text-phoenix-black hover:text-phoenix-gold transition"
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}

              {/* Mobile Contact Info */}
              <div className="border-t border-phoenix-beige/30 pt-4 mt-4 space-y-3">
                <motion.div
                  className="flex items-center gap-3 text-phoenix-black"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <FaPhone className="text-phoenix-gold" />
                  <a href="tel:+919949042692" className="hover:text-phoenix-gold transition-colors">
                    +91 9949042692
                  </a>
                </motion.div>
                <motion.div
                  className="flex items-center gap-3 text-phoenix-black"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.35 }}
                >
                  <FaMapMarkerAlt className="text-phoenix-gold" />
                  <span>Madhurawada, Visakhapatnam</span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default NavBar;
